import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
// Import the types from the generated API file
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { LegalEntityAddressDto, LegalEntityNameAliasDto, InvoiceStatusCountDto } from 'src/generated/services/TFinancialApi';

/**
 * BilledEntityItem interface with all properties from LegalEntityDetailsDto
 * plus the selected property for checkbox functionality
 */
export interface BilledEntityItem {
  /** @format int64 */
  id: number;
  name: string;
  code: string;
  active?: boolean;
  nationalCurrency?: string;
  /** @format int64 */
  parentId?: number;
  isBilledEntity?: boolean;
  addresses?: LegalEntityAddressDto[];
  nameAliases?: LegalEntityNameAliasDto[];
  subEntities?: BilledEntityItem[];
  /** List of invoice statuses associated with the service provider */
  invoiceStatuses?: InvoiceStatusCountDto[];
  /** Additional property for checkbox selection state */
  selected?: boolean;
}

// eslint-disable-next-line arrow-body-style
export const useBilledEntities = () => {
  return useQuery({
    queryKey: ['billed-entities-for-filter'],
    queryFn: async (): Promise<BilledEntityItem[]> => {
      try {
        const response = await tFinancialService.api.getListBilledEntityForFilter();

        if (response.data?.data) {
          // Recursive function to transform entities at any depth
          const transformEntity = (entity: any): BilledEntityItem => ({
            id: entity.id || 0,
            name: entity.name,
            code: entity.code,
            active: entity.active,
            nationalCurrency: entity.nationalCurrency,
            parentId: entity.parentId,
            isBilledEntity: entity.isBilledEntity,
            addresses: entity.addresses,
            nameAliases: entity.nameAliases,
            invoiceStatuses: entity.invoiceStatuses,
            subEntities: entity.subEntities?.map(transformEntity) || [],
            selected: false,
          });

          // Transform all root entities recursively
          return response.data.data.map(transformEntity);
        }

        return [];
      } catch (error) {
        console.error('Error fetching billed entities:', error);
        throw error;
      }
    },
  });
};

// Hook for managing selected billed entities
export const useSelectedBilledEntities = (initialEntities: BilledEntityItem[] = []) => {
  const [entities, setEntities] = useState<BilledEntityItem[]>(initialEntities);

  // Select or deselect a main entity and all its sub-entities
  const toggleMainEntity = (id: number) => {
    setEntities((prev) =>
      prev.map((entity) => {
        if (entity.id === id) {
          const newSelected = !entity.selected;
          return {
            ...entity,
            selected: newSelected,
            subEntities: entity.subEntities?.map((sub) => ({
              ...sub,
              selected: newSelected,
              subEntities: sub.subEntities?.map((subSub) => ({
                ...subSub,
                selected: newSelected,
              })),
            })),
          };
        }
        return entity;
      })
    );
  };

  // Select or deselect a sub-entity
  const toggleSubEntity = (mainId: number, subId: number) => {
    setEntities((prev) =>
      prev.map((entity) => {
        if (entity.id === mainId) {
          const updatedSubEntities = entity.subEntities?.map((sub) => {
            if (sub.id === subId) {
              const newSelected = !sub.selected;
              return {
                ...sub,
                selected: newSelected,
                subEntities: sub.subEntities?.map((subSub) => ({
                  ...subSub,
                  selected: newSelected,
                })),
              };
            }
            return sub;
          });

          // Check if all sub-entities are selected
          const allSubSelected = updatedSubEntities?.every((sub) => sub.selected) || false;

          return {
            ...entity,
            selected: allSubSelected,
            subEntities: updatedSubEntities,
          };
        }
        return entity;
      })
    );
  };

  // Select or deselect a level 2 sub-entity
  const toggleSubSubEntity = (mainId: number, subId: number, subSubId: number) => {
    setEntities((prev) => {
      // Now update the state with the new values
      return prev.map((entity) => {
        if (entity.id === mainId) {
          const updatedSubEntities = entity.subEntities?.map((sub) => {
            if (sub.id === subId) {
              // Find the current sub-sub entity to toggle
              const subSubToToggle = sub.subEntities?.find((ss) => ss.id === subSubId);
              const newSelected = !subSubToToggle?.selected;

              const updatedSubSubEntities = sub.subEntities?.map((subSub) => {
                if (subSub.id === subSubId) {
                  return { ...subSub, selected: newSelected };
                }
                return subSub;
              });

              // Check if all level 2 sub-entities are selected
              const allSubSubSelected = updatedSubSubEntities?.every((subSub) => subSub.selected) || false;

              return {
                ...sub,
                selected: allSubSubSelected,
                subEntities: updatedSubSubEntities,
              };
            }
            return sub;
          });

          // Check if all sub-entities are selected
          const allSubSelected = updatedSubEntities?.every((sub) => sub.selected) || false;

          return {
            ...entity,
            selected: allSubSelected,
            subEntities: updatedSubEntities,
          };
        }
        return entity;
      });
    });
  };

  // Select all entities
  const selectAll = () => {
    setEntities((prev) =>
      prev.map((entity) => ({
        ...entity,
        selected: true,
        subEntities: entity.subEntities?.map((sub) => ({
          ...sub,
          selected: true,
          subEntities: sub.subEntities?.map((subSub) => ({
            ...subSub,
            selected: true,
          })),
        })),
      }))
    );
  };

  // Clear all selections
  const clearAll = () => {
    setEntities((prev) =>
      prev.map((entity) => ({
        ...entity,
        selected: false,
        subEntities: entity.subEntities?.map((sub) => ({
          ...sub,
          selected: false,
          subEntities: sub.subEntities?.map((subSub) => ({
            ...subSub,
            selected: false,
          })),
        })),
      }))
    );
  };

  // Get all selected entities (flattened)
  const getSelectedEntities = () => {
    const selected: BilledEntityItem[] = [];

    entities.forEach((entity) => {
      if (entity.selected) {
        selected.push(entity);
      } else {
        entity.subEntities?.forEach((sub) => {
          if (sub.selected) {
            selected.push(sub);
          } else {
            // Check level 2 sub-entities
            sub.subEntities?.forEach((subSub) => {
              if (subSub.selected) {
                selected.push(subSub);
              }
            });
          }
        });
      }
    });

    return selected;
  };

  // Dynamic toggle function that works with any depth using path array
  const toggleEntityByPath = (path: number[]) => {
    if (path.length === 0) return;

    setEntities((prev) => {
      // Recursive function to update entity at path
      const updateEntityAtPath = (entities: BilledEntityItem[], pathIndex: number): BilledEntityItem[] => {
        if (pathIndex >= path.length) return entities;

        const targetId = path[pathIndex];
        const isLastInPath = pathIndex === path.length - 1;

        return entities.map((entity) => {
          if (entity.id === targetId) {
            if (isLastInPath) {
              // Toggle this entity and all descendants
              const newSelected = !entity.selected;
              const toggleAllDescendants = (ent: BilledEntityItem): BilledEntityItem => ({
                ...ent,
                selected: newSelected,
                subEntities: ent.subEntities?.map(toggleAllDescendants),
              });
              return toggleAllDescendants(entity);
            }
            // Continue down the path
            const updatedSubEntities = entity.subEntities ? updateEntityAtPath(entity.subEntities, pathIndex + 1) : entity.subEntities;
            // Check if all sub-entities are selected
            const allSubSelected = updatedSubEntities?.every((sub) => sub.selected) || false;
            return {
              ...entity,
              selected: allSubSelected,
              subEntities: updatedSubEntities,
            };
          }
          return entity;
        });
      };

      return updateEntityAtPath(prev, 0);
    });
  };

  return {
    entities,
    setEntities,
    toggleMainEntity,
    toggleSubEntity,
    toggleSubSubEntity,
    toggleEntityByPath, // New dynamic toggle function
    selectAll,
    clearAll,
    getSelectedEntities,
  };
};
